/** Server-sent events from a fetch body: `data: {"type", "payload"}` frames,
 * spec-correct (CRLF, multi-line data, comments, a final unterminated frame). */
export type StreamEvent = { type: string; payload: Record<string, unknown> }

export async function* parseSSEEvents(stream: ReadableStream<Uint8Array>): AsyncGenerator<StreamEvent> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let dataLines: string[] = []

  const dispatch = (): StreamEvent | null => {
    if (dataLines.length === 0) return null
    const data = dataLines.join("\n")
    dataLines = []
    try {
      const parsed: unknown = JSON.parse(data)
      if (!parsed || typeof parsed !== "object") return null
      const event = parsed as { type?: unknown; payload?: unknown }
      if (typeof event.type !== "string") return null
      const payload = event.payload && typeof event.payload === "object" && !Array.isArray(event.payload)
        ? event.payload as Record<string, unknown>
        : {}
      return { type: event.type, payload }
    } catch {
      return null
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      const lines = buffer.split(/\r?\n/)
      buffer = done ? "" : lines.pop() ?? ""
      if (done && buffer) lines.push(buffer)
      for (const line of lines) {
        if (line === "") {
          const event = dispatch()
          if (event) yield event
          continue
        }
        if (line.startsWith(":")) continue
        const separator = line.indexOf(":")
        const field = separator < 0 ? line : line.slice(0, separator)
        if (field === "data") dataLines.push(separator < 0 ? "" : line.slice(separator + 1).replace(/^ /, ""))
      }
      if (done) {
        const event = dispatch()
        if (event) yield event
        break
      }
    }
  } finally {
    reader.releaseLock()
  }
}
